// backgroundRemoval.js
import { removeBackground as imglyRemoveBackground } from '@imgly/background-removal';

/**
 * Removes the background from an image using @imgly/background-removal.
 * 
 * @param {string} imageBase64 - The base64 encoded image data
 * @param {Object} options - Configuration options
 * @returns {Promise<string>} - Promise resolving to the processed image with background removed
 */
export const removeImageBackground = async (imageBase64, options = {}) => {
  try {
    console.log('[backgroundRemoval] Starting image processing with @imgly/background-removal');
    
    // Extract the base64 data from the Data URL format
    const base64Data = imageBase64.split(',')[1];
    if (!base64Data) {
      throw new Error('Invalid image data format');
    }
    
    // Create a Blob from the base64 data
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    
    // Determine image type from Data URL
    const mimeType = imageBase64.match(/data:([^;]+)/)?.[1] || 'image/png';
    const imageBlob = new Blob([array], { type: mimeType });
    
    // Define preprocessing config
    const imglyOptions = {
      model: 'medium', // Use 'small' for faster processing or 'large' for better quality
      output: {
        format: 'image/png', // Always output PNG for transparency support
        quality: 0.8
      },
      progress: options.progress || ((progress) => {
        console.log(`[backgroundRemoval] Progress: ${Math.round(progress * 100)}%`);
      })
    };
    
    // Add debug mode if requested
    if (options.debug) {
      imglyOptions.debug = true;
    }
    
    // Process the image to remove background
    const resultBlob = await imglyRemoveBackground(imageBlob, imglyOptions);
    
    // Convert the result Blob back to base64
    const resultBase64 = await blobToBase64(resultBlob);
    
    console.log('[backgroundRemoval] Successfully removed background');
    return resultBase64;
  } catch (error) {
    console.error('[backgroundRemoval] Error removing background:', error);
    
    // In case of error, return the original image as a fallback
    console.warn('[backgroundRemoval] Falling back to original image');
    return imageBase64;
  }
};

/**
 * Convert a Blob to a base64 Data URL
 * 
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} - Promise resolving to the base64 Data URL
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
